import { emitter } from 'shared/helpers/mitt';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import numberSettings from '../../../../../config/masifi/whatsapp_qr_number_settings.yml';

// WhatsApp por código QR (Masifi). Toda la lógica frontend del canal vive en archivos nuestros; los archivos
// core solo importan de acá (touchpoints de import + 1 línea).
export const WHATSAPP_QR_PROVIDER = 'whatsapp_qr';
export const WHATSAPP_QR_FEATURE = 'feature_whatsapp_qr';
// Chequeo de número (spec polish-1 §3.8) = Masifi::WhatsappQr::ConversationKnobs::FEATURES[:number_check] (el spec del
// backend masifi_feature_flags_whatsapp_qr_polish_spec.rb fija el mismo nombre).
export const MASIFI_WA_NUMBER_CHECK_FEATURE = 'masifi_wa_number_check';
export const WHATSAPP_QR_EVENT = 'masifi.whatsapp_qr.updated';
// Estado grueso para los agentes del inbox (F2 ruling 15; backend Masifi::WhatsappQr::AgentStatus::EVENT).
export const WHATSAPP_QR_STATUS_EVENT = 'masifi.whatsapp_qr.status';
// Fase 2 (spec §5.5): cómo va el historial del teléfono de un número (backend History::Progress::EVENT, solo admins).
export const WHATSAPP_QR_HISTORY_EVENT = 'masifi.whatsapp_qr.history';

export const isWhatsappQrInbox = inbox =>
  inbox?.provider === WHATSAPP_QR_PROVIDER;

// Ajustes POR NÚMERO (spec polish-1 §4): misma fuente única que Masifi::WhatsappQr::NumberSettings.
export const MASIFI_WA_NUMBER_SETTINGS = Object.keys(numberSettings.fields);
export const MASIFI_WA_MARK_READ_MODES = numberSettings.fields.mark_read.values;
export const MASIFI_WA_PRESENCE_MODES = numberSettings.fields.presence.values;
// Fase 2: días hacia atrás del historial ('7' '30' '90' '365'), misma fuente que el backend.
export const MASIFI_WA_HISTORY_DAYS = numberSettings.fields.history_days.values;
export const MASIFI_WA_CALL_REPLY_MAX = Number(
  numberSettings.fields.call_reply.max
);
// Las claves de ajustes del payload del número (vienen planas junto al estado).
export const pickNumberSettings = data =>
  Object.fromEntries(
    MASIFI_WA_NUMBER_SETTINGS.filter(key => key in (data || {})).map(key => [
      key,
      data[key],
    ])
  );

// ComposeNewConversationForm.vue (upstream) uses this as its isWhatsapp: a QR inbox has no Business API templates,
// so compose treats it as a plain inbox. Reads the camelCase channelType of the form's targetInbox.
export const isNonQrWhatsappInbox = inbox =>
  inbox?.channelType === INBOX_TYPES.WHATSAPP && !isWhatsappQrInbox(inbox);

// ¿Hay una nota de voz grabada entre los adjuntos? El payload de un solo mensaje (el que usa QR) solo marcaba
// `isVoiceMessage` con direct uploads (bug de upstream); sin la marca el adjunto queda sin `is_voice_message`.
export const hasVoiceNote = files =>
  (files || []).some(file => file?.isVoiceMessage);

// Stickers recibidos (E2E F2 lote 3): el backend los guarda como imagen (webp 512px) y la burbuja nativa los dibujaba
// como fotos gigantes. El animado (`application/was`, un Lottie que el navegador no dibuja) llega como no soportado.
const ANIMATED_STICKER_MIME = 'application/was';

export const isMasifiWaAnimatedSticker = contentAttributes => {
  const media = contentAttributes?.masifiWaMedia;
  return media?.kind === 'sticker' && media?.mime === ANIMATED_STICKER_MIME;
};

// Burbuja de sticker: el estático (o todavía bajando) y el animado. Un estático que no se pudo bajar sigue siendo la
// burbuja nativa de no soportado.
export const isMasifiWaSticker = contentAttributes =>
  contentAttributes?.masifiWaMedia?.kind === 'sticker' &&
  (!contentAttributes.isUnsupported ||
    isMasifiWaAnimatedSticker(contentAttributes));

// E2E fix batch 2 (F3a): un marcador de "no se puede mostrar" con el motivo del conector (código de verificación, "ver
// una vez", plantilla de empresa…) muestra el POR QUÉ que escribió el servidor en el idioma de la cuenta (content) en vez
// del texto fijo de la burbuja nativa. Sin motivo (filas viejas, media que no bajó) sigue la nativa.
export const isMasifiWaUnsupportedWithReason = contentAttributes =>
  !!contentAttributes?.isUnsupported &&
  !!contentAttributes?.masifiWaUnsupportedReason;

// Ruta de la UI donde vive el panel de vinculación (Ajustes → Entradas → <entrada> → Configuración), armada con
// las etiquetas REALES del sidebar y de las pestañas: si upstream o nosotros las renombramos, la copia no driftea.
export const whatsappQrLinkPath = (t, inboxName) =>
  [
    t('SIDEBAR.SETTINGS'),
    t('SIDEBAR.INBOXES'),
    inboxName,
    t('INBOX_MGMT.TABS.CONFIGURATION'),
  ]
    .filter(Boolean)
    .join(' → ');

export const whatsappQrProviderOptions = (t, enabled) =>
  enabled
    ? [
        {
          key: WHATSAPP_QR_PROVIDER,
          title: t('MASIFI_WHATSAPP_QR.PROVIDER.TITLE'),
          description: t('MASIFI_WHATSAPP_QR.PROVIDER.DESCRIPTION'),
          icon: 'i-woot-whatsapp',
        },
      ]
    : [];

// ActionCable → bus de la app: el panel escucha con useEmitter(WHATSAPP_QR_EVENT, …).
export const onWhatsappQrUpdated = data =>
  emitter.emit(WHATSAPP_QR_EVENT, data);

// ActionCable → bus: el banner del composer escucha con useMasifiWhatsappQrStatus.
export const onWhatsappQrStatus = data =>
  emitter.emit(WHATSAPP_QR_STATUS_EVENT, data);

// ActionCable → bus: el panel del número escucha el progreso del historial.
export const onWhatsappQrHistory = data =>
  emitter.emit(WHATSAPP_QR_HISTORY_EVENT, data);

// actionCable.js (upstream) spreads this into its events map (import + 1 line).
export const masifiWhatsappQrCableEvents = {
  [WHATSAPP_QR_EVENT]: onWhatsappQrUpdated,
  [WHATSAPP_QR_STATUS_EVENT]: onWhatsappQrStatus,
  [WHATSAPP_QR_HISTORY_EVENT]: onWhatsappQrHistory,
};
